import React, { useCallback, useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Loader2, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';

const RESOURCE_TYPE = 'semantic_model';

const LEVELS = [
  { value: 'viewer', label: 'View' },
  { value: 'editor', label: 'Create & Edit' },
  { value: 'owner', label: 'Delete' },
  { value: 'none', label: 'Remove access' },
];

// Apply one grant (role or user + level) to several models at once. Reuses the
// per-model object-permission endpoints (looped) — no bulk backend needed.
export function BulkModelShareDialog({ open, onOpenChange, modelIds, onApplied }) {
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [grantee, setGrantee] = useState('');      // "role:analyst" | "user:user_x"
  const [level, setLevel] = useState('viewer');

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.getRoles(); setRoles(r || []); } catch (e) { setRoles([]); }
    try { const u = await api.getUsers(); setUsers(u || []); } catch (e) { setUsers([]); }
    setLoading(false);
  }, []);

  useEffect(() => { if (open) { load(); setGrantee(''); setLevel('viewer'); } }, [open, load]);

  const apply = async () => {
    if (!grantee) { toast.error('Pick a role or user'); return; }
    const [kind, id] = grantee.split(':');
    const target = kind === 'role' ? { role: id } : { user_id: id };
    setApplying(true);
    let ok = 0, fail = 0;
    for (const mid of modelIds) {
      try {
        if (level === 'none') await api.removeObjectPermission(RESOURCE_TYPE, mid, target);
        else await api.setObjectPermission(RESOURCE_TYPE, mid, { ...target, permission_level: level });
        ok += 1;
      } catch (e) { fail += 1; }
    }
    setApplying(false);
    if (ok) toast.success(`Updated ${ok} model${ok === 1 ? '' : 's'}${fail ? `, ${fail} failed` : ''}`);
    else toast.error('Could not update any model');
    onApplied && onApplied();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-md" data-testid="bulk-model-share-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-foreground flex items-center gap-2">
            <Shield className="w-4 h-4 text-primary" /> Share {modelIds.length} model{modelIds.length === 1 ? '' : 's'}
          </DialogTitle>
          <DialogDescription className="text-xs">
            Apply the same access to every selected model at once.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground text-xs gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <label className="text-[11px] text-muted-foreground">Role or user</label>
              <Select value={grantee} onValueChange={setGrantee}>
                <SelectTrigger className="mt-1 h-9 text-xs rounded-lg bg-muted/40 border-border/50" data-testid="bulk-share-grantee">
                  <SelectValue placeholder="Select a role or user" />
                </SelectTrigger>
                <SelectContent>
                  {roles.map((r) => (
                    <SelectItem key={`role:${r.role_name}`} value={`role:${r.role_name}`} className="text-xs capitalize">Role · {r.role_name}</SelectItem>
                  ))}
                  {users.map((u) => (
                    <SelectItem key={`user:${u.user_id}`} value={`user:${u.user_id}`} className="text-xs">User · {u.name || u.email}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[11px] text-muted-foreground">Access level</label>
              <Select value={level} onValueChange={setLevel}>
                <SelectTrigger className="mt-1 h-9 text-xs rounded-lg bg-muted/40 border-border/50" data-testid="bulk-share-level">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LEVELS.map((l) => (
                    <SelectItem key={l.value} value={l.value} className="text-xs">{l.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={apply} disabled={applying || !grantee} className="w-full rounded-lg bg-primary text-primary-foreground active:scale-95" data-testid="bulk-share-apply">
              {applying ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null}
              Apply to {modelIds.length} model{modelIds.length === 1 ? '' : 's'}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default BulkModelShareDialog;
